import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const AccountSection = () => {
  const { user, profile, refreshProfile, signOut } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [showPwds, setShowPwds] = useState(false);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setBusinessName(profile.business_name || '');
      setPhone(profile.phone_number || '');
    } else if (user?.user_metadata) {
      setFirstName(user.user_metadata.first_name || '');
      setLastName(user.user_metadata.last_name || '');
    }
  }, [profile, user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          first_name: firstName,
          last_name: lastName,
          business_name: businessName || null,
          phone_number: phone || null,
        });

      if (error) {
        setSaveMsg('Failed to save. Please try again.');
      } else {
        await refreshProfile();
        setSaveMsg('success');
      }
    } catch {
      setSaveMsg('Connection error. Please try again.');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 3000);
    }
  };

  const handleChangePassword = async () => {
    if (!newPwd || newPwd.length < 8) {
      setPwdMsg('New password must be at least 8 characters.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdMsg('Passwords do not match.');
      return;
    }
    setPwdSaving(true);
    setPwdMsg('');
    try {
      const { error } = await supabase.auth.updateUser({ password: newPwd });
      if (error) {
        setPwdMsg(error.message || 'Failed to update password.');
      } else {
        setPwdMsg('success');
        setCurrentPwd('');
        setNewPwd('');
        setConfirmPwd('');
      }
    } catch {
      setPwdMsg('Connection error. Please try again.');
    } finally {
      setPwdSaving(false);
      setTimeout(() => setPwdMsg(''), 3000);
    }
  };

  const inputClass = 'w-full bg-ea-black border border-ea-dark-border rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue transition-colors';
  const labelClass = 'block text-white/70 text-xs font-medium mb-1.5 uppercase tracking-wider';

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-white font-bold text-xl">Account Settings</h2>
        <p className="text-ea-gray text-sm mt-0.5">Manage your profile and account preferences</p>
      </div>

      {/* Profile section */}
      <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-4 pb-4 border-b border-ea-dark-border">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-ea-blue to-ea-blue-electric flex items-center justify-center">
            <span className="text-white font-bold text-lg">
              {[firstName, lastName].map((n) => n?.[0] || '').join('').toUpperCase() || 'M'}
            </span>
          </div>
          <div>
            <p className="text-white font-semibold">{firstName} {lastName}</p>
            <p className="text-ea-gray/60 text-xs">{user?.email}</p>
            <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full bg-ea-blue/10 border border-ea-blue/20">
              <i className="ri-vip-crown-line text-ea-blue text-[10px]"></i>
              <span className="text-ea-blue text-[10px] font-medium">Member</span>
            </div>
          </div>
        </div>

        <h3 className="text-white font-semibold text-sm">Personal Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>First Name</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} type="text" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Last Name</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} type="text" className={inputClass} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Email Address</label>
          <input value={user?.email || ''} type="email" disabled className="w-full bg-ea-black/50 border border-ea-dark-border/50 rounded-lg px-4 py-3 text-ea-gray text-sm cursor-not-allowed" />
          <p className="text-ea-gray/40 text-[10px] mt-1">Email cannot be changed. Contact support if needed.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Business Name</label>
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} type="text" placeholder="Your company" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Phone Number</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="+44 7700 000000" className={inputClass} />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          {saveMsg === 'success' && (
            <div className="flex items-center gap-2 text-green-400 text-xs">
              <i className="ri-check-line"></i> Profile updated successfully
            </div>
          )}
          {saveMsg && saveMsg !== 'success' && (
            <p className="text-red-400 text-xs">{saveMsg}</p>
          )}
          {!saveMsg && <span />}
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-5 py-2.5 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : <><i className="ri-save-line"></i> Save Changes</>}
          </button>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm">Change Password</h3>
          <button onClick={() => setShowPwds(!showPwds)} className="text-ea-blue text-xs hover:underline cursor-pointer">
            {showPwds ? 'Hide' : 'Change Password'}
          </button>
        </div>
        {showPwds && (
          <>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>New Password</label>
                <input value={newPwd} onChange={(e) => setNewPwd(e.target.value)} type="password" placeholder="Min. 8 characters" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Confirm New Password</label>
                <input value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} type="password" placeholder="Repeat new password" className={inputClass} />
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              {pwdMsg === 'success' && (
                <div className="flex items-center gap-2 text-green-400 text-xs">
                  <i className="ri-check-line"></i> Password updated
                </div>
              )}
              {pwdMsg && pwdMsg !== 'success' && <p className="text-red-400 text-xs">{pwdMsg}</p>}
              {!pwdMsg && <span />}
              <button
                onClick={handleChangePassword}
                disabled={pwdSaving}
                className="px-5 py-2.5 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {pwdSaving ? <><i className="ri-loader-4-line animate-spin"></i> Updating...</> : 'Update Password'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Danger zone */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
        <h3 className="text-white font-semibold text-sm mb-3">Sign Out</h3>
        <p className="text-ea-gray text-xs mb-4">Sign out of your Everything Automation account on this device.</p>
        <button
          onClick={signOut}
          className="px-5 py-2.5 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 w-fit"
        >
          <i className="ri-logout-box-r-line"></i>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default AccountSection;
